// Admin console: Penyimpanan Pluto (/admin/penyimpanan-pluto, plan
// 2026-10-21 Fase A). The one central Google account ("Drive Pluto") where
// every new chat upload and generated file is stored, one folder per user.
//   result     notice          back from Google: connected, or why not (?connected=1 / ?error=)
//   status     plain spacing   connected account, since when and by whom, or not connected
//   warnings   notice          Login Google incomplete, no encryption key
//   actions    card            Hubungkan / Hubungkan ulang, Tes koneksi, Putuskan (confirm)
//   folder     card            Folder data pengguna: where each user's folder is made; Pilih folder
//                              (Google Picker as the central account) or a pasted link
//   about      card            what is stored where; older files stay in each user's own Drive
import { useId, useState, type FormEvent } from "react";
import * as API from "../api";
import { formatDateTime } from "../lib/format";
import { navigate, useLocation } from "../router";
import { googleNav } from "../shell/GoogleSignIn";
import { Badge, Dialog, Field, Notice, PageHeader, SkeletonRows, useAsync, useTitle } from "../ui/components";
import { Glyph } from "../ui/icons";
import { googlePicker } from "./google-picker";

/** Messages for ?error=<code> after the Google round trip. */
export const PLUTO_DRIVE_ERRORS: Record<string, string> = {
  not_configured: "Pengaturan Login Google belum lengkap. Lengkapi dulu di halaman Login Google.",
  denied: "Persetujuan di Google dibatalkan.",
  state: "Sesi menghubungkan sudah kedaluwarsa atau sudah dipakai. Coba Hubungkan lagi.",
  not_admin: "Selesaikan langkah ini dengan akun admin yang menekan Hubungkan.",
  exchange: "Google menolak permintaan. Coba Hubungkan lagi.",
  id_token: "Identitas akun Google tidak dapat diperiksa. Coba Hubungkan lagi.",
  missing_scope: "Izin Google Drive tidak diberikan. Centang akses Google Drive di halaman persetujuan Google.",
  no_refresh_token: "Google tidak memberikan izin akses jangka panjang. Coba Hubungkan lagi.",
  encryption: "Server belum memiliki kunci enkripsi, sehingga koneksi tidak dapat disimpan.",
  unavailable: "Google sedang tidak dapat dihubungi. Coba lagi sebentar.",
};

const TEST_REASON: Record<string, string> = {
  not_connected: "Drive Pluto belum terhubung atau aksesnya dicabut. Hubungkan ulang.",
  unavailable: "Google Drive sedang tidak dapat dihubungi. Coba lagi sebentar.",
};

export function plutoStatusLine(d: API.PlutoDrive): string {
  if (d.status === "ok") return `Terhubung ke ${d.email ?? "akun Google"}`;
  if (d.status === "revoked") return "Koneksi terputus, file baru belum dapat disimpan";
  return "Belum terhubung, file baru belum dapat disimpan";
}

export function PlutoDriveScreen() {
  useTitle("Penyimpanan Pluto");
  const drive = useAsync(() => API.getPlutoDrive(), []);
  const location = useLocation();
  const returned = location.query.get("connected") === "1" ? "connected" : location.query.get("error");
  const d = drive.data;

  return (
    <>
      <PageHeader
        divided
        title="Penyimpanan Pluto"
        lead="Akun Google pusat tempat semua lampiran dan dokumen baru pengguna disimpan, satu folder per pengguna."
      />
      {returned ? (
        <Notice
          tone={returned === "connected" ? "success" : "danger"}
          title={returned === "connected" ? "Drive Pluto terhubung" : "Drive Pluto belum terhubung"}
          action={
            <button type="button" className="btn btn-ghost" onClick={() => navigate(location.path, { replace: true })}>
              Tutup
            </button>
          }
        >
          <p>{returned === "connected" ? "File baru pengguna sekarang disimpan di akun ini." : (PLUTO_DRIVE_ERRORS[returned] ?? "Coba Hubungkan lagi.")}</p>
        </Notice>
      ) : null}
      {drive.loading && !d ? (
        <SkeletonRows count={3} label="Memuat status penyimpanan" />
      ) : drive.error ? (
        <Notice tone="danger" title="Status belum dapat dimuat" action={<button type="button" className="btn btn-secondary" onClick={drive.reload}>Muat ulang</button>}>
          <p>{API.errorMessage(drive.error)}</p>
        </Notice>
      ) : d ? (
        <>
          <section className="region" aria-label="Status Drive Pluto">
            <p className="quiet-line" data-pluto-status={d.status}>
              <Glyph name={d.status === "ok" ? "tickCircle" : "infoCircle"} />
              <span>
                <strong>{plutoStatusLine(d)}</strong>.
                {d.status === "ok" && d.connectedAt ? ` Dihubungkan ${formatDateTime(d.connectedAt)}${d.connectedBy ? ` oleh ${d.connectedBy.name}` : ""}.` : ""}
              </span>
            </p>
            {!d.loginConfigured ? (
              <Notice tone="warning" title="Login Google belum lengkap">
                <p>
                  Drive Pluto memakai klien OAuth yang sama dengan Login Google. Isi Client ID dan Client Secret di halaman{" "}
                  <a href="/admin/login-google">Login Google</a> terlebih dahulu.
                </p>
              </Notice>
            ) : null}
            {!d.encryptionAvailable ? (
              <Notice tone="warning" title="Kunci enkripsi server belum diatur">
                <p>Koneksi Drive Pluto tidak dapat disimpan sampai server memiliki kunci enkripsi. Hubungi pengelola server.</p>
              </Notice>
            ) : null}
          </section>
          <ActionsCard drive={d} onChange={drive.set} />
          {d.status === "ok" ? <UsersFolderCard drive={d} onChange={drive.set} /> : null}
          <section className="card form-card" aria-labelledby="pd-about">
            <h2 id="pd-about" className="card-title">
              Isi penyimpanan
            </h2>
            <ul className="rows">
              <li className="row">
                <span className="row-main">
                  <span className="row-title">{d.usersFolder ? d.usersFolder.name || "Folder pilihan" : "Pluto/Users"}/nama pengguna</span>
                  <span className="row-meta">Lampiran chat, dokumen yang dibuat, dan folder Project tiap pengguna.</span>
                </span>
              </li>
              <li className="row">
                <span className="row-main">
                  <span className="row-title">Belum ada batas per pengguna</span>
                  <span className="row-meta">Semua pengguna berbagi ruang akun Google ini (akun gratis 15 GB). Batas unggah per jam dan per hari tetap berlaku.</span>
                </span>
              </li>
              <li className="row">
                <span className="row-main">
                  <span className="row-title">File lama tetap di Drive pengguna</span>
                  <span className="row-meta">File yang dikirim sebelum Drive Pluto tetap dapat diunduh lewat Google Drive pengguna masing-masing.</span>
                </span>
              </li>
            </ul>
          </section>
        </>
      ) : null}
    </>
  );
}

function ActionsCard({ drive, onChange }: { drive: API.PlutoDrive; onChange: (d: API.PlutoDrive) => void }) {
  const [busy, setBusy] = useState<"connect" | "test" | "disconnect" | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [notice, setNotice] = useState<{ tone: "success" | "warning" | "danger"; title: string; body: string } | null>(null);
  const connected = drive.status === "ok";

  async function connect() {
    setBusy("connect");
    setNotice(null);
    try {
      googleNav.assign(await API.startPlutoDrive());
    } catch (err) {
      setNotice({ tone: "danger", title: "Belum dapat menghubungkan", body: API.errorMessage(err) });
      setBusy(null);
    }
  }

  async function test() {
    setBusy("test");
    setNotice(null);
    try {
      const result = await API.testPlutoDrive();
      setNotice(
        result.ok
          ? { tone: "success", title: "Koneksi berfungsi", body: "Folder Pluto di Google Drive dapat dibaca dan ditulisi." }
          : { tone: "warning", title: "Koneksi belum berfungsi", body: TEST_REASON[result.reason ?? ""] ?? "Coba Hubungkan ulang." },
      );
      if (!result.ok) onChange(await API.getPlutoDrive());
    } catch (err) {
      setNotice({ tone: "danger", title: "Tes belum dapat dijalankan", body: API.errorMessage(err) });
    } finally {
      setBusy(null);
    }
  }

  async function disconnect() {
    setBusy("disconnect");
    setNotice(null);
    try {
      onChange(await API.disconnectPlutoDrive());
      setConfirming(false);
      setNotice({ tone: "success", title: "Drive Pluto diputuskan", body: "Akses aplikasi dicabut di Google. File yang sudah tersimpan tetap ada di akun itu." });
    } catch (err) {
      setNotice({ tone: "danger", title: "Belum dapat memutuskan", body: API.errorMessage(err) });
    } finally {
      setBusy(null);
    }
  }

  return (
    <section className="card form-card" aria-labelledby="pd-actions">
      <h2 id="pd-actions" className="card-title">
        Koneksi
      </h2>
      <p className="quiet-line">
        {connected ? <Badge tone="success">Terhubung</Badge> : <Badge tone="warning">{drive.status === "revoked" ? "Terputus" : "Belum terhubung"}</Badge>}
        <span>{drive.email ?? "Belum ada akun"}</span>
      </p>
      {drive.rootFolderUrl ? (
        <p>
          <a href={drive.rootFolderUrl} target="_blank" rel="noreferrer noopener">
            {drive.usersFolder ? "Buka folder data pengguna di Google Drive" : "Buka folder Pluto di Google Drive"}
          </a>
        </p>
      ) : null}
      {notice ? (
        <Notice tone={notice.tone} title={notice.title}>
          <p>{notice.body}</p>
        </Notice>
      ) : null}
      <div className="button-row">
        <button
          type="button"
          className={connected ? "btn btn-secondary" : "btn"}
          aria-busy={busy === "connect" || undefined}
          disabled={busy !== null || !drive.loginConfigured || !drive.encryptionAvailable}
          onClick={connect}
        >
          {connected || drive.status === "revoked" ? "Hubungkan ulang" : "Hubungkan Google"}
        </button>
        {connected ? (
          <>
            <button type="button" className="btn btn-secondary" aria-busy={busy === "test" || undefined} disabled={busy !== null} onClick={test}>
              Tes koneksi
            </button>
            <button type="button" className="btn btn-secondary" disabled={busy !== null} onClick={() => setConfirming(true)}>
              Putuskan
            </button>
          </>
        ) : null}
      </div>
      <Dialog
        open={confirming}
        size="sm"
        title="Putuskan Drive Pluto?"
        description="Akses aplikasi dicabut di Google. Pengguna tidak dapat mengirim lampiran atau menyimpan dokumen sampai akun dihubungkan lagi. File yang sudah tersimpan tetap ada."
        busy={busy === "disconnect"}
        onClose={() => setConfirming(false)}
        footer={
          <>
            <button type="button" className="btn btn-secondary" disabled={busy !== null} onClick={() => setConfirming(false)}>
              Batal
            </button>
            <button type="button" className="btn" data-tone="danger" aria-busy={busy === "disconnect" || undefined} onClick={disconnect}>
              Ya, putuskan
            </button>
          </>
        }
      >
        <span />
      </Dialog>
    </section>
  );
}

/** Folder data pengguna: the Drive folder each user's folder is made in, picked with Google Picker or pasted as a link. */
function UsersFolderCard({ drive, onChange }: { drive: API.PlutoDrive; onChange: (d: API.PlutoDrive) => void }) {
  const id = useId();
  const [link, setLink] = useState("");
  const [busy, setBusy] = useState<"pick" | "save" | null>(null);
  const [notice, setNotice] = useState<{ tone: "success" | "danger"; title: string; body: string } | null>(null);
  const folder = drive.usersFolder;

  async function store(value: string, how: "pick" | "save") {
    setBusy(how);
    setNotice(null);
    try {
      const next = await API.setPlutoDriveUsersFolder(value);
      onChange(next);
      setLink("");
      setNotice({ tone: "success", title: "Folder disimpan", body: `Folder tiap pengguna dibuat langsung di ${next.usersFolder?.name || "folder pilihan"}. File baru pengguna disimpan di sana.` });
    } catch (err) {
      setNotice({ tone: "danger", title: "Folder belum dapat dipakai", body: API.errorMessage(err) });
    } finally {
      setBusy(null);
    }
  }

  async function pick() {
    setBusy("pick");
    setNotice(null);
    try {
      const picked = await googlePicker.pickFolder(await API.getPlutoDrivePicker());
      if (!picked) {
        setBusy(null);
        return;
      }
      await store(picked.id, "pick");
    } catch (err) {
      setNotice({ tone: "danger", title: "Pemilih folder belum dapat dibuka", body: API.errorMessage(err) });
      setBusy(null);
    }
  }

  function submit(e: FormEvent) {
    e.preventDefault();
    if (link.trim()) void store(link.trim(), "save");
  }

  return (
    <section className="card form-card" aria-labelledby="pd-folder">
      <h2 id="pd-folder" className="card-title">
        Folder data pengguna
      </h2>
      <p className="quiet-line" data-users-folder={folder ? "set" : "default"}>
        <Glyph name="folder" />
        {folder ? (
          <span>
            Folder tiap pengguna dibuat langsung di <strong>{folder.name || "folder pilihan"}</strong>.{" "}
            <a href={folder.url} target="_blank" rel="noreferrer noopener">
              Buka folder
            </a>
          </span>
        ) : (
          <span>Belum dipilih. Saat ini folder tiap pengguna dibuat di Pluto/Users pada My Drive akun ini.</span>
        )}
      </p>
      {notice ? (
        <Notice tone={notice.tone} title={notice.title}>
          <p>{notice.body}</p>
        </Notice>
      ) : null}
      {drive.pickerAvailable ? (
        <div className="button-row">
          <button type="button" className="btn" aria-busy={busy === "pick" || undefined} disabled={busy !== null} onClick={() => void pick()}>
            <Glyph name="folderAdd" />
            <span>{folder ? "Ganti folder" : "Pilih folder"}</span>
          </button>
        </div>
      ) : (
        <Notice tone="warning" title="Pemilih folder Google belum diatur">
          <p>Isi GOOGLE_PICKER_API_KEY dan GOOGLE_PICKER_APP_ID di server agar folder dapat dipilih. Folder yang dibuat manual hanya dapat dipakai setelah dipilih lewat pemilih ini.</p>
        </Notice>
      )}
      <form className="form" onSubmit={submit}>
        <Field id={`${id}-link`} label="Atau tempel link folder" hint="Untuk folder yang sudah pernah dipilih atau dibuat oleh Pluto, misalnya saat memulihkan pengaturan.">
          <input
            id={`${id}-link`}
            type="url"
            inputMode="url"
            placeholder="https://drive.google.com/drive/folders/..."
            value={link}
            aria-describedby={`${id}-link-hint`}
            onChange={(e) => setLink(e.currentTarget.value)}
          />
        </Field>
        <div className="button-row">
          <button type="submit" className="btn btn-secondary" aria-busy={busy === "save" || undefined} disabled={busy !== null || link.trim() === ""}>
            Simpan folder
          </button>
        </div>
      </form>
      <p className="quiet-line">
        <Glyph name="infoCircle" />
        <span>Mengganti folder hanya berlaku untuk file baru. File yang sudah tersimpan tetap di tempatnya dan tetap dapat diunduh.</span>
      </p>
    </section>
  );
}
